import { PART_LIBRARY_CAPABILITIES } from '../parts/components/part-configurator-subject'
import type {
  PartConfiguratorCapabilities,
  PartConfiguratorSubject,
} from '../parts/components/part-configurator-subject'
import type { RfqReviewLine } from './review-domain'

/**
 * UID-434/WS-D — the `RfqReviewLine -> PartConfiguratorSubject` adapter, the
 * RFQ sibling `fromLibraryPartRow` (`part-configurator-subject.ts`)
 * anticipated. Seeds the shell's Configuration-tab form from
 * `line.specSelection.specification` — the TYPED field, never the deprecated
 * `attributes` bag (ADR-0117 D5).
 *
 * Fields this line's contract has NO counterpart for are simply OMITTED,
 * never fabricated: no `dfmSummary` (RFQ has no DFM concept pre-quote — see
 * `RFQ_LINE_CONFIGURATOR_CAPABILITIES.dfmCapable`), no `attachmentsPartId`
 * (no addressable part id to mount `PartAttachmentsSection` against), no
 * `rowStatus`/`filterStatus` (a Part Library listing concept this line
 * doesn't have). `drawingFilename` is `null` — `ReviewLineSourceable`
 * carries no such field. `thumbnailUrl` DOES have a counterpart as of
 * UID-335 follow-up (`ReviewLineSourceable.imageRef` — the same
 * root-relative, same-origin-safe value `PartsReviewTable`'s own thumbnail
 * tile renders), so the shell's Configuration-tab preview shows the same
 * image the review table does instead of always going blank.
 */
export function fromRfqReviewLine(line: RfqReviewLine): PartConfiguratorSubject {
  const specification = line.specSelection?.specification
  const configAttributes: Readonly<Record<string, unknown>> | undefined = specification
    ? {
        process: specification.process,
        material: specification.material ?? '',
        finish: specification.finish ?? '',
        ...(specification.inspections && specification.inspections.length > 0
          ? { inspections: [...specification.inspections] }
          : {}),
        ...(specification.certificates && specification.certificates.length > 0
          ? { certificates: [...specification.certificates] }
          : {}),
        ...(specification.customInspection ? { customInspection: true } : {}),
      }
    : undefined

  return {
    id: line.id,
    displayName: line.name,
    thumbnailUrl: line.sourceable.imageRef ?? null,
    drawingFilename: null,
    configAttributes,
    quantity: line.quantity,
    ...(line.sourceable.indicativePrice
      ? { indicativePrice: line.sourceable.indicativePrice }
      : {}),
    // The shell's own `PricingProjection` (`parts/types.ts`) declares
    // `lockedRfqId`/`lockedQuoteId` as plain optional strings, never
    // nullable — narrower than the wire's `string | null` (RFQ has no use
    // for distinguishing "absent" from "explicitly null" here, so both
    // collapse to "omitted").
    ...(line.sourceable.pricingProjection
      ? {
          pricingProjection: {
            mode: line.sourceable.pricingProjection.mode,
            ...(line.sourceable.pricingProjection.lockedRfqId
              ? { lockedRfqId: line.sourceable.pricingProjection.lockedRfqId }
              : {}),
            ...(line.sourceable.pricingProjection.lockedQuoteId
              ? { lockedQuoteId: line.sourceable.pricingProjection.lockedQuoteId }
              : {}),
            ...(line.sourceable.pricingProjection.volumeCurve
              ? { volumeCurve: line.sourceable.pricingProjection.volumeCurve }
              : {}),
          },
        }
      : {}),
    ...(line.name ? { sceneKey: line.name } : {}),
  }
}

/**
 * UID-434/WS-D — the RFQ single-line entry point's capability set. Per the
 * human D3 ruling carried into this ticket, the two data-less tabs
 * (Manufacturability feedback, Drawing analysis) stay VISIBLE with an honest
 * empty state rather than being hidden — `dfmCapable`/`attachmentsCapable`
 * are `false`, never omitted.
 *
 * `applyToMultiple` is `false` — "Apply to multiple parts" is a Part Library
 * concept (an active multi-selection in that listing) an RFQ review line has
 * no equivalent of.
 *
 * The field-set asymmetry (UID-403 bug 3's shape, closed here by
 * construction): `RfqLineSpecification`
 * (`apps/api/openapi.dist.yaml:4030`) carries process/material/finish/
 * inspections/certificates/customInspection ONLY — no `color`, `tolerance`,
 * `threadedHoleCount`, or `additionalRequirements` (all four are
 * `EditPartConfigRequest`-only, ADR-0117 D9.1). `colorCapable`/
 * `threadedHolesCapable`/`additionalRequirementsCapable` are therefore
 * `false` — the shell OMITS those controls rather than rendering an edit a
 * save can never persist. `customInspectionCapable` is `true` — the mirror
 * image, RFQ-only.
 */
export const RFQ_LINE_CONFIGURATOR_CAPABILITIES: PartConfiguratorCapabilities = {
  applyToMultiple: false,
  dfmCapable: false,
  attachmentsCapable: false,
  colorCapable: false,
  threadedHolesCapable: false,
  additionalRequirementsCapable: false,
  customInspectionCapable: true,
  // RFQ never mounts the shell's BUILT-IN `ConfigPricingFooter` (it supplies
  // its own `RfqLineConfiguratorPricingFooter`, which renders this same
  // manual-quote wording independently — see that file), so this value is
  // never actually read through this seam. Set to `'manualQuote'` anyway for
  // capability-set completeness/honesty: an RFQ line's `'rfq'` state really
  // is the manual-quote case, never a Cart-style price reset.
  rfqFooterCopyKind: 'manualQuote',
}

// Re-exported so a caller/test that wants to sanity-check "Part Library still
// persists everything this shell can render a control for" can import both
// capability sets from one place without reaching into the parts vertical.
export { PART_LIBRARY_CAPABILITIES }
